/**
 * Job Posting Page
 * Employers can post new jobs with category selection
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Briefcase,
  Building2,
  MapPin,
  DollarSign,
  Clock,
  Users,
  ChevronRight,
  Loader2,
  Plus,
  Sparkles,
  Star,
  Zap,
  Check,
  AlertCircle,
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Work style options
const WORK_STYLES = [
  { value: 'on-site', label: 'On-site' },
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
];

// Employment type options
const EMPLOYMENT_TYPES = [
  { value: 'full-time', label: 'Full-time' },
  { value: 'part-time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'temporary', label: 'Temporary' },
  { value: 'internship', label: 'Internship' },
];

// Experience levels
const EXPERIENCE_LEVELS = [
  { value: 'entry', label: 'Entry Level (0-1 years)' },
  { value: 'junior', label: 'Junior (1-3 years)' },
  { value: 'mid', label: 'Mid Level (3-5 years)' },
  { value: 'senior', label: 'Senior (5-10 years)' },
  { value: 'lead', label: 'Lead/Manager (10+ years)' },
];

const JobPostingPage = () => {
  const navigate = useNavigate();
  const { country } = useCountry();
  const { toast } = useToast();
  
  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAuthenticated = !!localStorage.getItem('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [categories, setCategories] = useState([]);
  const [step, setStep] = useState(1);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    company_name: '',
    company_logo: '',
    location: '',
    work_style: 'on-site',
    employment_type: 'full-time',
    salary_min: '',
    salary_max: '',
    salary_negotiable: false,
    experience_required: '',
    education_required: '',
    skills_required: '',
    benefits: '',
    application_email: user?.email || '',
    application_url: '',
    application_deadline: '',
    is_featured: false,
    is_urgent: false,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to post a job',
      });
      navigate('/auth');
      return;
    }
    fetchConfig();
  }, [isAuthenticated, country]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/job-posting/config/${country}`);
      
      if (response.data.success) {
        setConfig(response.data.config);
        setCategories(response.data.config.categories || []);
        
        if (!response.data.config.enabled) {
          toast({
            title: 'Job posting disabled',
            description: 'Job posting is currently disabled for your country',
            variant: 'destructive',
          });
        }
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast({
        title: 'Error',
        description: 'Failed to load job posting configuration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateTotalFee = () => {
    if (!config) return 0;
    let total = config.posting_fee || 0;
    if (formData.is_featured) total += config.featured_fee || 0;
    if (formData.is_urgent) total += config.urgent_fee || 0;
    return total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.category || !formData.description || !formData.company_name || !formData.location) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setSubmitting(true);
      
      const payload = {
        ...formData,
        salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
        skills_required: formData.skills_required ? formData.skills_required.split(',').map(s => s.trim()).filter(Boolean) : [],
        benefits: formData.benefits ? formData.benefits.split(',').map(b => b.trim()).filter(Boolean) : [],
        employer_id: user.id,
        employer_email: user.email,
        employer_phone: user.phone || '',
        country: country,
      };
      
      const response = await axios.post(`${API_URL}/api/job-posting/create`, payload);
      
      if (response.data.success) {
        toast({
          title: 'Job posted successfully!',
          description: `Your job posting is now ${response.data.status === 'active' ? 'live' : response.data.status}`,
        });
        navigate('/jobs/employer-dashboard');
      }
    } catch (error) {
      console.error('Error posting job:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to post job',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!config?.enabled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Job Posting Unavailable</h2>
            <p className="text-gray-600 mb-4">
              Job posting is currently disabled for your country. Please try again later.
            </p>
            <Button onClick={() => navigate('/jobs')}>Browse Jobs</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="job-posting-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-2 text-emerald-200 text-sm mb-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/jobs" className="hover:text-white">Jobs</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Post a Job</span>
          </div>
          
          <h1 className="text-3xl font-bold mb-2">
            <Plus className="inline h-8 w-8 mr-2" />
            Post a New Job
          </h1>
          <p className="text-emerald-100">
            Reach thousands of qualified candidates
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 -mt-4">
        <div className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-center gap-4">
          {[
            { num: 1, label: 'Job Details' },
            { num: 2, label: 'Company Info' },
            { num: 3, label: 'Requirements' },
            { num: 4, label: 'Review & Post' },
          ].map((s, i) => (
            <div key={s.num} className="flex items-center">
              <button
                onClick={() => setStep(s.num)}
                className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm transition-colors ${
                  step === s.num
                    ? 'bg-emerald-100 text-emerald-700 font-semibold'
                    : step > s.num
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {step > s.num ? <Check className="h-4 w-4" /> : <span>{s.num}</span>}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < 3 && <ChevronRight className="h-4 w-4 text-gray-300 mx-2" />}
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit}>
          {/* Step 1: Job Details */}
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-emerald-600" />
                  Job Details
                </CardTitle>
                <CardDescription>Basic information about the position</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Senior Software Engineer"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    data-testid="job-title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => handleInputChange('category', v)}>
                    <SelectTrigger data-testid="job-category-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.slug} value={cat.slug}>
                          {cat.icon} {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="description">Job Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe the role, responsibilities, and what makes this opportunity exciting..."
                    rows={6}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    data-testid="job-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="work_style">Work Style</Label>
                    <Select value={formData.work_style} onValueChange={(v) => handleInputChange('work_style', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WORK_STYLES.map((ws) => (
                          <SelectItem key={ws.value} value={ws.value}>{ws.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="employment_type">Employment Type</Label>
                    <Select value={formData.employment_type} onValueChange={(v) => handleInputChange('employment_type', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EMPLOYMENT_TYPES.map((et) => (
                          <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="button" onClick={() => setStep(2)}>
                    Next: Company Info
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Company Info */}
          {step === 2 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-emerald-600" />
                  Company Information
                </CardTitle>
                <CardDescription>Tell candidates about your company</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    placeholder="Your company name"
                    value={formData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    data-testid="company-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Select value={formData.location} onValueChange={(v) => handleInputChange('location', v)}>
                    <SelectTrigger data-testid="job-location-select">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {(config?.locations || []).map((loc) => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.location === 'other' && (
                    <Input
                      className="mt-2"
                      placeholder="Enter location"
                      onChange={(e) => handleInputChange('location', e.target.value)}
                    />
                  )}
                </div>

                <div>
                  <Label htmlFor="company_logo">Company Logo URL (optional)</Label>
                  <Input
                    id="company_logo"
                    placeholder="https://example.com/logo.png"
                    value={formData.company_logo}
                    onChange={(e) => handleInputChange('company_logo', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="application_email">Application Email</Label>
                  <Input
                    id="application_email"
                    type="email"
                    placeholder="hr@company.com"
                    value={formData.application_email}
                    onChange={(e) => handleInputChange('application_email', e.target.value)}
                    data-testid="application-email-input"
                  />
                </div>

                <div className="flex justify-between">
                  <Button type="button" variant="outline" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button type="button" onClick={() => setStep(3)}>
                    Next: Requirements
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Requirements */}
          {step === 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-emerald-600" />
                  Requirements & Compensation
                </CardTitle>
                <CardDescription>What are you looking for in candidates?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="salary_min">Salary Min ({config?.currency_symbol})</Label>
                    <Input
                      id="salary_min"
                      type="number"
                      placeholder="e.g., 50000"
                      value={formData.salary_min}
                      onChange={(e) => handleInputChange('salary_min', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="salary_max">Salary Max ({config?.currency_symbol})</Label>
                    <Input
                      id="salary_max"
                      type="number"
                      placeholder="e.g., 80000"
                      value={formData.salary_max}
                      onChange={(e) => handleInputChange('salary_max', e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="salary_negotiable"
                    checked={formData.salary_negotiable}
                    onCheckedChange={(checked) => handleInputChange('salary_negotiable', checked)}
                  />
                  <Label htmlFor="salary_negotiable">Salary is negotiable</Label>
                </div>

                <div>
                  <Label htmlFor="experience_required">Experience Level</Label>
                  <Select value={formData.experience_required} onValueChange={(v) => handleInputChange('experience_required', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select experience level" />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPERIENCE_LEVELS.map((exp) => (
                        <SelectItem key={exp.value} value={exp.value}>{exp.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="skills_required">Required Skills (comma separated)</Label>
                  <Input
                    id="skills_required"
                    placeholder="e.g., JavaScript, React, Node.js"
                    value={formData.skills_required}
                    onChange={(e) => handleInputChange('skills_required', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="benefits">Benefits (comma separated)</Label>
                  <Input
                    id="benefits"
                    placeholder="e.g., Health insurance, Remote work, Flexible hours"
                    value={formData.benefits}
                    onChange={(e) => handleInputChange('benefits', e.target.value)}
                  />
                </div>

                <div className="flex justify-between">
                  <Button type="button" variant="outline" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <Button type="button" onClick={() => setStep(4)}>
                    Next: Review
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Review & Post */}
          {step === 4 && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-emerald-600" />
                    Review Your Job Posting
                  </CardTitle>
                  <CardDescription>Make sure everything looks good before posting</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Preview */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{formData.title || 'Job Title'}</h3>
                        <p className="text-gray-600">{formData.company_name || 'Company Name'}</p>
                      </div>
                      <div className="flex gap-2">
                        {formData.is_urgent && (
                          <Badge className="bg-red-100 text-red-700">
                            <Zap className="h-3 w-3 mr-1" /> Urgent
                          </Badge>
                        )}
                        {formData.is_featured && (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Star className="h-3 w-3 mr-1" /> Featured
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {formData.location || 'Location'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        {formData.employment_type}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        {formData.salary_min && formData.salary_max
                          ? `${config?.currency_symbol}${parseInt(formData.salary_min).toLocaleString()} - ${config?.currency_symbol}${parseInt(formData.salary_max).toLocaleString()}`
                          : formData.salary_negotiable ? 'Negotiable' : 'Not specified'
                        }
                      </span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {formData.description?.slice(0, 300) || 'Job description...'}
                      {formData.description?.length > 300 && '...'}
                    </p>
                  </div>

                  {/* Upgrade Options */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">Boost Your Job Posting</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            id="is_featured"
                            checked={formData.is_featured}
                            onCheckedChange={(checked) => handleInputChange('is_featured', checked)}
                          />
                          <div>
                            <Label htmlFor="is_featured" className="flex items-center gap-2 cursor-pointer">
                              <Star className="h-4 w-4 text-amber-500" />
                              Featured Listing
                            </Label>
                            <p className="text-xs text-gray-500">Get 3x more visibility</p>
                          </div>
                        </div>
                        <span className="font-semibold">
                          {config?.featured_fee > 0 ? `${config?.currency_symbol}${config?.featured_fee}` : 'Free'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            id="is_urgent"
                            checked={formData.is_urgent}
                            onCheckedChange={(checked) => handleInputChange('is_urgent', checked)}
                          />
                          <div>
                            <Label htmlFor="is_urgent" className="flex items-center gap-2 cursor-pointer">
                              <Zap className="h-4 w-4 text-red-500" />
                              Urgent Hiring
                            </Label>
                            <p className="text-xs text-gray-500">Show urgency badge</p>
                          </div>
                        </div>
                        <span className="font-semibold">
                          {config?.urgent_fee > 0 ? `${config?.currency_symbol}${config?.urgent_fee}` : 'Free'}
                        </span>
                      </div>
                    </div>

                    {calculateTotalFee() > 0 && (
                      <div className="mt-4 pt-4 border-t flex items-center justify-between">
                        <span className="text-lg font-semibold">Total</span>
                        <span className="text-2xl font-bold text-emerald-600">
                          {config?.currency_symbol}{calculateTotalFee().toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-between">
                <Button type="button" variant="outline" onClick={() => setStep(3)}>
                  Back
                </Button>
                <Button 
                  type="submit" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={submitting}
                  data-testid="post-job-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Posting...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Post Job {calculateTotalFee() > 0 ? `(${config?.currency_symbol}${calculateTotalFee()})` : ''}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default JobPostingPage;
